import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject,Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { MediaclipError } from 'app/shared/components/mediaclip-error';
import {
    DeleteLocaleStoreFileInput,
    DeleteStoreCustomizationFileInput,
    GraphDotnetService,
    graphql,
    MediaclipMutationDeleteStoreDesignerCssArgs,
    MediaclipMutationDeleteStoreDesignerJavascriptArgs,
    MediaclipMutationDeleteStoreDesignerLocaleArgs,
    MediaclipMutationDeleteStoreDesignerLogoArgs,
    MediaclipMutationUpdateStoreDesignerCssArgs,
    MediaclipMutationUpdateStoreDesignerJavascriptArgs,
    MediaclipMutationUpdateStoreDesignerLocaleArgs,
    MediaclipMutationUpdateStoreDesignerLogoArgs,
    StoreDesignerCustomization,
    UpdateLocaleStoreFilesInput,
    UpdateStoreBinaryFilesInput,
    UpdateStoreFilesInput
} from 'app/shared/graph';

@Injectable({ providedIn: 'root' })
export class DesignerCustomizationsService {
    protected readonly graphDotnetService = inject(GraphDotnetService);
    private readonly httpClient = inject(HttpClient);


    getCustomizationFile(url: string) {
        return this.httpClient
            .get(url, {
                responseType: 'text'
            })
            .pipe(
                catchError((err) => {
                    if (err instanceof HttpErrorResponse && err.status === 404) {
                        return of('');
                    }
                    throw new MediaclipError(`Unable to load customization file at ${url}`, { innerError: err });
                })
            );
    }

    updateJavascriptFile(input: UpdateStoreFilesInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateStoreDesignerJavascriptArgs>(graphql`
                mutation updateStoreDesignerJavascript($input: UpdateStoreFilesInput!) {
                    updateStoreDesignerJavascript(input: $input)
                }
            `, { input }
        ).pipe(map((result) => result.updateStoreDesignerJavascript!));
    }

    deleteJavascriptFile(input: DeleteStoreCustomizationFileInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationDeleteStoreDesignerJavascriptArgs>(graphql`
                mutation deleteStoreDesignerJavascript($input: DeleteStoreCustomizationFileInput!) {
                    deleteStoreDesignerJavascript(input: $input)
                }
            `, { input }
        ).pipe(map((result) => result.deleteStoreDesignerJavascript!));
    }

    updateCssFile(input: UpdateStoreFilesInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateStoreDesignerCssArgs>(graphql`
                mutation updateStoreDesignerCss($input: UpdateStoreFilesInput!) {
                    updateStoreDesignerCss(input: $input)
                }
            `, { input }
        ).pipe(map((result) => result.updateStoreDesignerCss!));
    }

    deleteCssFile(input: DeleteStoreCustomizationFileInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationDeleteStoreDesignerCssArgs>(graphql`
                mutation deleteStoreDesignerCss($input: DeleteStoreCustomizationFileInput!) {
                    deleteStoreDesignerCss(input: $input)
                }
            `, { input }
        ).pipe(map((result) => result.deleteStoreDesignerCss!));
    }

    updateLocale(input: UpdateLocaleStoreFilesInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateStoreDesignerLocaleArgs>(graphql`
                mutation updateStoreDesignerLocale($input: UpdateLocaleStoreFilesInput!) {
                    updateStoreDesignerLocale(input: $input)
                }
            `, { input }
        ).pipe(map((result) => result.updateStoreDesignerCss!));
    }

    deleteLocale(input: DeleteLocaleStoreFileInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationDeleteStoreDesignerLocaleArgs>(graphql`
                mutation deleteStoreDesignerLocale($input: DeleteLocaleStoreFileInput!) {
                    deleteStoreDesignerLocale(input: $input)
                }
            `, { input }
        ).pipe(map((result) => result.deleteStoreDesignerCss!));
    }

    updateLogo(input: UpdateStoreBinaryFilesInput): Observable<any> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateStoreDesignerLogoArgs>(graphql`
                mutation updateStoreDesignerLogo($input: UpdateStoreBinaryFilesInput!) {
                    updateStoreDesignerLogo(input: $input)
                }
            `, { input }
        ).pipe(map((result) => result.updateStoreDesignerLogo!));
    }

    deleteLogo(input: DeleteStoreCustomizationFileInput): Observable<any> {
        return this.graphDotnetService.mutate<MediaclipMutationDeleteStoreDesignerLogoArgs>(graphql`
                mutation deleteStoreDesignerLogo($input: DeleteStoreCustomizationFileInput!) {
                    deleteStoreDesignerLogo(input: $input)
                }
            `, { input }
        ).pipe(map((result) => result.deleteStoreDesignerLogo!));
    }

    getStoreFiles(storeId: string): Observable<StoreDesignerCustomization> {
        return this.graphDotnetService.query(graphql`
                query getStoreFiles($storeId: String!) {
                    store(id: $storeId) {
                        designerCustomization {
                            css {
                                filename
                                url
                            }
                            logo {
                                filename
                                url
                            }
                            javascript {
                                filename
                                url
                            }
                            locales {
                                culture
                                filename
                                url
                            }
                        }
                    }
                }
            `,
            { storeId }
        ).pipe(map(r => r.store.designerCustomization));
    }
}
